import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, theme as antdTheme } from 'antd';
import { ThemeProvider, useTheme } from '@/theme/ThemeContext';
import ThemeSync from '@/theme/ThemeSync';
import { KeycloakProvider } from '@/auth/KeycloakProvider';
import ProtectedRoute from '@/auth/ProtectedRoute';
import AppShell from '@/components/layout/AppShell';
import DashboardPage from '@/pages/Dashboard/DashboardPage';
import OpportunityListPage from '@/pages/Opportunities/OpportunityListPage';
import OpportunityCreatePage from '@/pages/Opportunities/OpportunityCreatePage';
import OpportunityDetailPage from '@/pages/Opportunities/OpportunityDetailPage';
import RfoDetailPage from '@/pages/RfoDetail/RfoDetailPage';
import ContentLibraryPage from '@/pages/ContentLibrary/ContentLibraryPage';
import UserManagementPage from '@/pages/Admin/UserManagementPage';
import RaciConfigPage from '@/pages/Admin/RaciConfigPage';
import SystemSettingsPage from '@/pages/Admin/SystemSettingsPage';
import ActionItemsPage from '@/pages/ActionItems/ActionItemsPage';
import NotificationsPage from '@/pages/Notifications/NotificationsPage';
import PostSubmissionPage from '@/pages/PostSubmission/PostSubmissionPage';
import ProfilePage from '@/pages/Profile/ProfilePage';
import TaskBoardPage from '@/pages/TaskBoard/TaskBoardPage';
import TalentPoolPage from '@/pages/TalentPool/TalentPoolPage';
import DiscussionsPage from '@/pages/Discussions/DiscussionsPage';
import LoginPage from '@/pages/Auth/LoginPage';

const buildTheme = (isDark: boolean) => ({
  algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
  token: {
    colorPrimary: isDark ? '#818CF8' : '#4F46E5',
    colorLink: isDark ? '#818CF8' : '#4F46E5',
    colorSuccess: isDark ? '#4ADE80' : '#16A34A',
    colorWarning: isDark ? '#FBBF24' : '#D97706',
    colorError: isDark ? '#F87171' : '#DC2626',
    colorInfo: isDark ? '#818CF8' : '#4F46E5',
    borderRadius: 10,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', system-ui, sans-serif",
    fontSize: 14,
    colorBgContainer: isDark ? '#1A2436' : '#FFFFFF',
    colorBgElevated: isDark ? '#243044' : '#FFFFFF',
    colorBgLayout: isDark ? '#0B1120' : '#F4F6F9',
    colorBorder: isDark ? '#2D3A4F' : '#E4E9F0',
    colorBorderSecondary: isDark ? '#232E42' : '#F1F5F9',
    colorText: isDark ? '#E5E9F0' : '#0F172A',
    colorTextSecondary: isDark ? '#94A3B8' : '#475569',
    colorTextDisabled: isDark ? '#64748B' : '#475569',
    colorBgContainerDisabled: isDark ? '#232E42' : '#F1F5F9',
    controlHeight: 36,
    lineHeight: 1.6,
  },
  components: {
    Card: {
      borderRadiusLG: 12,
      boxShadowTertiary: isDark
        ? '0 1px 4px 0 rgba(0,0,0,0.4), 0 1px 2px -1px rgba(0,0,0,0.3)'
        : '0 1px 4px 0 rgba(0,0,0,0.07), 0 1px 2px -1px rgba(0,0,0,0.04)',
    },
    Button: {
      borderRadius: 8,
      controlHeight: 36,
      colorTextDisabled: isDark ? '#64748B' : '#475569',
      colorBgContainerDisabled: isDark ? '#232E42' : '#F1F5F9',
      borderColorDisabled: isDark ? '#2D3A4F' : '#CBD5E1',
    },
    Table: {
      borderRadius: 8,
      headerBg: isDark ? '#141C2B' : '#F8FAFC',
      rowHoverBg: isDark ? '#232E42' : '#F8FAFC',
    },
    Menu: {
      itemBorderRadius: 8,
    },
    Input: {
      borderRadius: 8,
    },
    Select: {
      borderRadius: 8,
    },
    Tag: {
      borderRadiusSM: 6,
    },
    Layout: {
      siderBg: '#0F172A',
      triggerBg: '#1E293B',
    },
  },
});

function ThemedApp() {
  const { resolvedTheme } = useTheme();
  return (
    <ConfigProvider theme={buildTheme(resolvedTheme === 'dark')}>
      <BrowserRouter>
        <Routes>
          {/* All routes — Keycloak initialized (check-sso, no forced redirect) */}
          <Route
            path="/*"
            element={
          <KeycloakProvider>
            <ThemeSync />
            <Routes>
            {/* Login page — accessible without authentication */}
            <Route path="login" element={<LoginPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="action-items" element={<ActionItemsPage />} />

              <Route path="opportunities">
                <Route index element={<OpportunityListPage />} />
                <Route path="new" element={<OpportunityCreatePage />} />
                <Route path=":id" element={<OpportunityDetailPage />} />
              </Route>

              <Route path="rfo/:id" element={<RfoDetailPage />} />

              <Route
                path="task-board"
                element={
                  <ProtectedRoute requiredRoles={['ADMIN', 'CEO']}>
                    <TaskBoardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="talent-pool"
                element={
                  <ProtectedRoute requiredRoles={['ADMIN', 'REQUIREMENT_RESOURCING_MGR']}>
                    <TalentPoolPage />
                  </ProtectedRoute>
                }
              />
              <Route path="discussions" element={<DiscussionsPage />} />
              <Route path="content-library" element={<ContentLibraryPage />} />

              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="post-submission" element={<PostSubmissionPage />} />
              <Route path="profile" element={<ProfilePage />} />

              <Route path="admin">
                <Route
                  path="users"
                  element={
                    <ProtectedRoute requiredRoles={['ADMIN', 'CEO']}>
                      <UserManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="raci"
                  element={
                    <ProtectedRoute requiredRoles={['ADMIN']}>
                      <RaciConfigPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <ProtectedRoute requiredRoles={['ADMIN']}>
                      <SystemSettingsPage />
                    </ProtectedRoute>
                  }
                />
              </Route>

              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
            </Routes>
          </KeycloakProvider>
            }
          />
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <ThemedApp />
    </ThemeProvider>
  );
}

export default App;
